<template>
	<div id="afiliation-page" data-aos="fade">
		<!-- Confirmación -->
		<template v-if="submitted">
			<section class="top-section">
				<div class="container">
					<div class="row">
						<div class="col-12 col-lg-10 col-xl-8 mx-auto text-center">
							<p class="hero-eyebrow">Afiliación</p>
							<h1 class="hero-title">¡Solicitud enviada exitosamente!</h1>
						</div>
					</div>
				</div>
			</section>

			<section class="content-section">
				<div class="container">
					<div class="row">
						<div class="col-12 col-lg-10 col-xl-8 mx-auto">
							<div class="form-card confirmation-card text-center">
								<div class="success-icon-wrap mb-4">
									<div class="success-icon">
										<i class="fa-regular fa-circle-check"></i>
									</div>
								</div>

								<p class="confirmation-lead mb-4">
									Gracias,
									<span class="text-primary font-weight-bold">
										{{ formData.nombre }} {{ formData.apellidos }}
									</span>,
									por tu interés en unirte a unlawk.
								</p>

								<div class="next-steps text-left mb-4">
									<h3 class="next-steps-title">
										<i class="fa-regular fa-sparkles mr-2"></i>
										¿Qué sigue ahora?
									</h3>
									<ul class="list-unstyled mb-0">
										<li class="next-step-item">
											<span class="step-num">1</span>
											<span>Nuestro equipo revisará tu solicitud en las próximas 48 horas</span>
										</li>
										<li class="next-step-item">
											<span class="step-num">2</span>
											<span>
												Te contactaremos a
												<strong>{{ formData.email }}</strong>
												con los siguientes pasos
											</span>
										</li>
										<li class="next-step-item">
											<span class="step-num">3</span>
											<span>
												Si tu perfil es aprobado, te ayudaremos a configurar tu cuenta y comenzar a recibir clientes
											</span>
										</li>
									</ul>
								</div>

								<div class="row mb-4">
									<div class="col-md-6 mb-3 mb-md-0">
										<div class="info-mini-card h-100">
											<i class="fa-regular fa-envelope info-mini-icon"></i>
											<h4 class="info-mini-title">Revisa tu correo</h4>
											<p class="info-mini-text mb-0">
												Te enviamos un email de confirmación con más detalles
											</p>
										</div>
									</div>
									<div class="col-md-6">
										<div class="info-mini-card h-100">
											<i class="fa-regular fa-phone info-mini-icon"></i>
											<h4 class="info-mini-title">Mantente atento</h4>
											<p class="info-mini-text mb-0">
												Podríamos contactarte al {{ formData.telefono }}
											</p>
										</div>
									</div>
								</div>

								<div class="d-flex flex-column flex-sm-row justify-content-center confirmation-actions pt-2">
									<b-button
										to="/"
										variant="outline-secondary"
										class="mb-2 mb-sm-0 mr-sm-2"
									>
										<i class="fa-regular fa-house mr-2"></i>
										Volver al inicio
									</b-button>
									<b-button
										to="/directorio"
										variant="info"
									>
										<i class="fa-regular fa-user mr-2"></i>
										Ver directorio de abogados
									</b-button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</template>

		<!-- Formulario -->
		<template v-else>
			<section class="top-section">
				<div class="container">
					<div class="row">
						<div class="col-12 col-lg-10 col-xl-8 mx-auto text-center">
							<p class="hero-eyebrow">Afiliación</p>
							<h1 class="hero-title">
								<span class="text-accent">Solicitud de Afiliación</span>
							</h1>
							<p class="hero-lead mx-auto">
								Completa tu solicitud para unirte al equipo de abogados de unlawk
							</p>
						</div>
					</div>
				</div>
			</section>

			<section class="content-section">
				<div class="container">
					<div class="row">
						<div class="col-12 col-lg-10 col-xl-9 mx-auto">
							<div class="form-card">
								<!-- Progreso -->
								<div class="progress-block mb-4">
									<div class="d-flex justify-content-between align-items-center mb-2">
										<span class="progress-label">Paso {{ currentStep }} de {{ totalSteps }}</span>
										<span class="progress-label">{{ progressPercent }}%</span>
									</div>
									<b-progress
										:value="progressPercent"
										:max="100"
										height="0.75rem"
										class="mb-3"
										variant="info"
									></b-progress>

									<div class="d-flex justify-content-between step-indicators">
										<div
											v-for="step in totalSteps"
											:key="step"
											class="step-indicator text-center"
											:class="stepIndicatorClass(step)"
										>
											<div class="step-circle">
												<i
													v-if="step < currentStep"
													class="fa-regular fa-circle-check"
												></i>
												<i
													v-else
													:class="stepIcons[step - 1]"
												></i>
											</div>
											<span class="step-label d-none d-md-block">Paso {{ step }}</span>
										</div>
									</div>
								</div>

								<b-form @submit.prevent="handleSubmit">
									<div class="step-content">
										<!-- Paso 1: Personal -->
										<div v-if="currentStep === 1" class="step-panel">
											<div class="step-header text-center mb-4">
												<div class="step-header-icon">
													<i class="fa-regular fa-user"></i>
												</div>
												<h3 class="step-title">Información Personal</h3>
												<p class="step-subtitle mb-0">Cuéntanos sobre ti</p>
											</div>

											<div class="row">
												<div class="col-md-6">
													<b-form-group label-for="nombre">
														<template #label>
															<i class="fa-regular fa-user mr-1 text-primary"></i>
															Nombre(s) *
														</template>
														<b-form-input
															id="nombre"
															v-model="formData.nombre"
															placeholder="Tu nombre"
															required
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-6">
													<b-form-group label-for="apellidos">
														<template #label>
															<i class="fa-regular fa-user mr-1 text-primary"></i>
															Apellidos *
														</template>
														<b-form-input
															id="apellidos"
															v-model="formData.apellidos"
															placeholder="Tus apellidos"
															required
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-6">
													<b-form-group label-for="email">
														<template #label>
															<i class="fa-regular fa-envelope mr-1 text-primary"></i>
															Correo Electrónico *
														</template>
														<b-form-input
															id="email"
															v-model="formData.email"
															type="email"
															placeholder="correo@ejemplo.com"
															required
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-6">
													<b-form-group label-for="telefono">
														<template #label>
															<i class="fa-regular fa-phone mr-1 text-primary"></i>
															Teléfono *
														</template>
														<b-form-input
															id="telefono"
															v-model="formData.telefono"
															type="tel"
															placeholder="+52 123 456 7890"
															required
														></b-form-input>
													</b-form-group>
												</div>
											</div>
										</div>

										<!-- Paso 2: Profesional -->
										<div v-else-if="currentStep === 2" class="step-panel">
											<div class="step-header text-center mb-4">
												<div class="step-header-icon">
													<i class="fa-regular fa-graduation-cap"></i>
												</div>
												<h3 class="step-title">Información Profesional</h3>
												<p class="step-subtitle mb-0">Tu formación y experiencia</p>
											</div>

											<div class="row">
												<div class="col-md-6">
													<b-form-group label-for="cedula">
														<template #label>
															<i class="fa-regular fa-award mr-1 text-primary"></i>
															Cédula Profesional *
														</template>
														<b-form-input
															id="cedula"
															v-model="formData.cedula"
															placeholder="12345678"
															required
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-6">
													<b-form-group label-for="universidad">
														<template #label>
															<i class="fa-regular fa-graduation-cap mr-1 text-primary"></i>
															Universidad *
														</template>
														<b-form-input
															id="universidad"
															v-model="formData.universidad"
															placeholder="Universidad Nacional..."
															required
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-6">
													<b-form-group label-for="anoGraduacion">
														<template #label>
															<i class="fa-regular fa-calendar mr-1 text-primary"></i>
															Año de Graduación
														</template>
														<b-form-input
															id="anoGraduacion"
															v-model="formData.anoGraduacion"
															type="number"
															min="1950"
															max="2026"
															placeholder="2020"
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-6">
													<b-form-group label-for="experiencia">
														<template #label>
															<i class="fa-regular fa-briefcase mr-1 text-primary"></i>
															Años de Experiencia *
														</template>
														<b-form-select
															id="experiencia"
															v-model="formData.experiencia"
															:options="experienciaOptions"
															required
														></b-form-select>
													</b-form-group>
												</div>
											</div>
										</div>

										<!-- Paso 3: Especialidades y ubicación -->
										<div v-else-if="currentStep === 3" class="step-panel">
											<div class="step-header text-center mb-4">
												<div class="step-header-icon">
													<i class="fa-regular fa-location-dot"></i>
												</div>
												<h3 class="step-title">Especialidades y Ubicación</h3>
												<p class="step-subtitle mb-0">¿En qué áreas del derecho te especializas?</p>
											</div>

											<div class="mb-4">
												<label class="especialidades-label d-block mb-2">
													<i class="fa-regular fa-file-lines mr-1 text-primary"></i>
													Especialidades Legales *
												</label>
												<p class="text-muted small mb-3">Selecciona al menos una especialidad</p>
												<div class="row">
													<div
														v-for="especialidad in especialidadesLegales"
														:key="especialidad"
														class="col-md-4 mb-2"
													>
														<label
															class="especialidad-option"
															:class="{ selected: formData.especialidades.includes(especialidad) }"
														>
															<b-form-checkbox
																v-model="formData.especialidades"
																:value="especialidad"
															>
																{{ especialidad }}
															</b-form-checkbox>
														</label>
													</div>
												</div>
											</div>

											<div class="row">
												<div class="col-md-4">
													<b-form-group label-for="estado">
														<template #label>
															<i class="fa-regular fa-location-dot mr-1 text-primary"></i>
															Estado *
														</template>
														<b-form-input
															id="estado"
															v-model="formData.estado"
															placeholder="Ciudad de México"
															required
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-4">
													<b-form-group label-for="ciudad">
														<template #label>
															<i class="fa-regular fa-building mr-1 text-primary"></i>
															Ciudad *
														</template>
														<b-form-input
															id="ciudad"
															v-model="formData.ciudad"
															placeholder="CDMX"
															required
														></b-form-input>
													</b-form-group>
												</div>
												<div class="col-md-4">
													<b-form-group label-for="despacho">
														<template #label>
															<i class="fa-regular fa-building mr-1 text-primary"></i>
															Despacho (Opcional)
														</template>
														<b-form-input
															id="despacho"
															v-model="formData.despacho"
															placeholder="Mi Despacho Legal"
														></b-form-input>
													</b-form-group>
												</div>
											</div>
										</div>

										<!-- Paso 4: Motivación -->
										<div v-else-if="currentStep === 4" class="step-panel">
											<div class="step-header text-center mb-4">
												<div class="step-header-icon">
													<i class="fa-regular fa-message"></i>
												</div>
												<h3 class="step-title">Motivación y Términos</h3>
												<p class="step-subtitle mb-0">Último paso para completar tu solicitud</p>
											</div>

											<b-form-group label-for="motivacion">
												<template #label>
													<i class="fa-regular fa-message mr-1 text-primary"></i>
													¿Por qué quieres unirte a unlawk? *
												</template>
												<b-form-textarea
													id="motivacion"
													v-model="formData.motivacion"
													rows="6"
													no-resize
													placeholder="Cuéntanos qué te motiva a formar parte de nuestra plataforma y cómo crees que podemos ayudarte a crecer profesionalmente..."
													required
												></b-form-textarea>
												<small class="form-text text-muted">Mínimo 100 caracteres</small>
											</b-form-group>

											<div class="summary-box mb-4">
												<h4 class="summary-title">Resumen de tu Solicitud</h4>
												<div class="row">
													<div class="col-md-6 mb-3">
														<p class="summary-label mb-0">Nombre completo</p>
														<p class="summary-value mb-0">
															{{ formData.nombre }} {{ formData.apellidos }}
														</p>
													</div>
													<div class="col-md-6 mb-3">
														<p class="summary-label mb-0">Correo</p>
														<p class="summary-value mb-0">{{ formData.email }}</p>
													</div>
													<div class="col-md-6 mb-3">
														<p class="summary-label mb-0">Cédula Profesional</p>
														<p class="summary-value mb-0">{{ formData.cedula }}</p>
													</div>
													<div class="col-md-6 mb-3">
														<p class="summary-label mb-0">Universidad</p>
														<p class="summary-value mb-0">{{ formData.universidad }}</p>
													</div>
													<div class="col-md-6 mb-3">
														<p class="summary-label mb-0">Experiencia</p>
														<p class="summary-value mb-0">{{ formData.experiencia }} años</p>
													</div>
													<div class="col-md-6 mb-3">
														<p class="summary-label mb-0">Ubicación</p>
														<p class="summary-value mb-0">
															{{ formData.ciudad }}, {{ formData.estado }}
														</p>
													</div>
													<div class="col-12">
														<p class="summary-label mb-2">Especialidades</p>
														<div class="especialidades-tags">
															<span
																v-for="esp in formData.especialidades"
																:key="esp"
																class="especialidad-tag"
															>
																{{ esp }}
															</span>
														</div>
													</div>
												</div>
											</div>

											<div class="terminos-box">
												<b-form-checkbox
													id="terminos"
													v-model="formData.aceptaTerminos"
													required
												>
													Acepto los
													<router-link to="/terminos-y-condiciones">términos y condiciones</router-link>
													y el
													<router-link to="/aviso-de-privacidad">aviso de privacidad</router-link>
													de unlawk *
												</b-form-checkbox>
											</div>
										</div>
									</div>

									<!-- Navegación -->
									<div class="form-nav d-flex justify-content-between pt-4 mt-2">
										<b-button
											type="button"
											variant="outline-secondary"
											:disabled="currentStep === 1"
											@click="prevStep"
										>
											<i class="fa-regular fa-chevron-left mr-1"></i>
											Anterior
										</b-button>

										<b-button
											v-if="currentStep < totalSteps"
											type="button"
											variant="info"
											@click="nextStep"
										>
											Siguiente
											<i class="fa-regular fa-chevron-right ml-1"></i>
										</b-button>
										<b-button
											v-else
											type="submit"
											variant="info"
											class="font-weight-bold px-4"
										>
											<i class="fa-regular fa-circle-check mr-1"></i>
											Enviar Solicitud
										</b-button>
									</div>
								</b-form>
							</div>
						</div>
					</div>
				</div>
			</section>
		</template>
	</div>
</template>

<script>
export default {
	data() {
		return {
			currentStep: 1,
			totalSteps: 4,
			submitted: false,
			stepIcons: [
				'fa-regular fa-user',
				'fa-regular fa-graduation-cap',
				'fa-regular fa-location-dot',
				'fa-regular fa-message',
			],
			experienciaOptions: [
				{ value: '', text: 'Selecciona tu experiencia', disabled: true },
				{ value: '1-3', text: '1-3 años' },
				{ value: '4-7', text: '4-7 años' },
				{ value: '8-15', text: '8-15 años' },
				{ value: '15+', text: 'Más de 15 años' },
			],
			especialidadesLegales: [
				'Derecho Civil',
				'Derecho Penal',
				'Derecho Laboral',
				'Derecho Mercantil',
				'Derecho Familiar',
				'Derecho Fiscal',
				'Derecho Administrativo',
				'Derecho Constitucional',
				'Derecho Internacional',
				'Propiedad Intelectual',
				'Derecho Ambiental',
				'Derecho Corporativo',
			],
			formData: {
				nombre: '',
				apellidos: '',
				email: '',
				telefono: '',
				cedula: '',
				universidad: '',
				anoGraduacion: '',
				especialidades: [],
				experiencia: '',
				estado: '',
				ciudad: '',
				despacho: '',
				motivacion: '',
				aceptaTerminos: false,
			},
		}
	},

	computed: {
		progressPercent() {
			return Math.round((this.currentStep / this.totalSteps) * 100)
		},
	},

	methods: {
		stepIndicatorClass(step) {
			if (step === this.currentStep) {
				return 'is-current'
			}
			if (step < this.currentStep) {
				return 'is-done'
			}
			return 'is-pending'
		},

		nextStep() {
			if (this.currentStep < this.totalSteps) {
				this.currentStep += 1
			}
		},

		prevStep() {
			if (this.currentStep > 1) {
				this.currentStep -= 1
			}
		},

		handleSubmit() {
			console.log('Formulario enviado:', this.formData)
			this.submitted = true
			window.scrollTo(0, 0)
		},
	},
}
</script>
